import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ChangableComponent } from '@app/models/changable.component';
import { CLibraryFilterData } from '@app/store/models';
import { getMe } from '@app/store/reducers/profile.reducer';
import { AccountService, bffRoutes, BffService, FeaturesService, UtilsService } from '@core/services/';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { loadFeaturings } from '@store/actions/featuring.actions';
import { getFeaturingsByKey } from '@store/reducers/featuring.reducer';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import { EContentPaneFormat, IContentPane, TenantEnum, User, UserStatusEnum } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { auditTime, takeUntil, filter, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-panes',
  templateUrl: './panes.component.html',
  styleUrls: ['./panes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanesComponent extends ChangableComponent implements OnInit, OnDestroy {
  public panes: IContentPane[] = [];
  public showNotFound = false;
  public contentFilter: CLibraryFilterData;
  public loading = {};
  public paneCounter = 0;
  public paneContentCounter = 0;
  public paneHasContentCounter = 0;

  private _me: User;
  private _isGuest: boolean;
  private loadContentBlocksSubject = new Subject<void>();

  public paneCategories: IContentPane = {
    always_think_as_ampty: true,
    order: 1,
    format: EContentPaneFormat['categories'],
    displayMode: 'simple',
    loaded: true,
  };

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    private bffService: BffService,
    private features: FeaturesService,
    private readonly store: Store,
    public utilsService: UtilsService,
    protected readonly cdr: ChangeDetectorRef,
    public deviceService: DeviceDetectorService,
  ) {
    super(cdr);
  }

  public ngOnInit(): void {
    let _getLibraryRouteData: string;

    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this._isGuest = me?.status === UserStatusEnum.guest;
          this.detectChanges();
        },
      });

    this.store
      .select(getLibraryRouteData)
      .pipe(
        auditTime(300),
        filter((data) => {
          // защита от обновления если настройки панелей не менялись
          const newGetLibraryRouteData = JSON.stringify(data);
          if (_getLibraryRouteData !== newGetLibraryRouteData) {
            _getLibraryRouteData = newGetLibraryRouteData;
            return true;
          }
          return false;
        }),
        tap((data) => {
          this.contentFilter = data;
          if (!bffRoutes.includes(this.contentFilter.baseRoute)) {
            this.panes = []; // Legacy reset shit
            this.store.dispatch(loadFeaturings({ contentFilter: this.contentFilter }));
          }
          this.resetVariables();
          this.loadContentBlocks$();
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  /*
   */
  private setNewPaneContent(new_content: any) {
    this.panes = new_content;
    this.detectChanges();
  }

  /*
   */
  private resetVariables() {
    // Todo. Temporary tricky solution
    const filter_chapters = 'lessons'; // For now only lessons. Need to check.
    const url = window.location.href;
    if (!url.match(filter_chapters)) {
      this.panes = [];
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (this.panes && this.panes[0] && this.panes[0].format === 'categories') {
      const cat_view = this.panes[0];
      this.panes = [];
      this.panes.push(cat_view);
      return;
    }

    this.panes = [];
  }

  /*
   */
  private loadContentBlocks$() {
    // Отменяем предыдущую подписку
    this.loadContentBlocksSubject.next();

    this.loading[this.contentFilter.baseRoute] = true;
    this.paneContentCounter = 0;
    this.paneHasContentCounter = 0;
    this.paneCounter = 0;
    this.detectChanges();

    if (bffRoutes.includes(this.contentFilter.baseRoute)) {
      this.bffService
        .bff(this.contentFilter, this.contentFilter.baseRoute)
        .pipe(
          filter((panes) => {
            this.showNotFound = panes.length === 0;
            return !!panes.length;
          }),
          tap((res: IContentPane[]) => {
            if (res && this.loading[this.contentFilter.baseRoute]) {
              this.loading[this.contentFilter.baseRoute] = false;
              this.paneCounter = res.length;
              this.showNotFound = res.length === 0;
              this.setNewPaneContent(res);
            }
          }),
          takeUntil(this.loadContentBlocksSubject),
          takeUntil(this.destroyed$),
        )
        .subscribe({
          error: (err) => {
            console.log('PANE ERROR', err);
          },
        });
    } else {
      this.store
        .select(getFeaturingsByKey, { contentFilter: this.contentFilter })
        .pipe(
          tap((res) => {
            if (res && this.loading[this.contentFilter.baseRoute]) {
              this.loading[this.contentFilter.baseRoute] = false;
              this.panes = res.data;
              this.paneCounter = res.data.length;
              this.showNotFound = res.data.length === 0;
              this.detectChanges();
            }
          }),
          takeUntil(this.loadContentBlocksSubject),
          takeUntil(this.destroyed$),
        )
        .subscribe();
    }
  }

  public paneLoaded(pane: IContentPane, hasContent: boolean) {
    if (pane.format === EContentPaneFormat.categories) {
      return;
    }
    this.paneContentCounter++;
    if (hasContent) {
      this.paneHasContentCounter++;
    }

    if (pane?.baseRoute === 'main' && pane?.contentFilter?.term !== '') {
      if (!pane?.payload__content.length && hasContent) {
        this.paneHasContentCounter--;
      }
      if (this.paneHasContentCounter > 0 && this.paneContentCounter === this.paneCounter) {
        this.showNotFound = false;
      } else {
        if (this.paneContentCounter === this.paneCounter) {
          this.showNotFound = true;
        }
      }
    } else {
      if (this.paneHasContentCounter > 0) {
        this.showNotFound = false;
      } else {
        this.showNotFound = true;
      }
    }

    if (this.isMeVersion()) {
      this.panes = this.panes.filter((el) => el.format !== EContentPaneFormat['my-classes-homework']);
      if (AccountService.isAsIfTeacher(this._me)) {
        this.panes = this.panes.filter((el) => el.format !== EContentPaneFormat['my-classes-tutors']);
      } else {
        this.panes = this.panes.filter((el) => el.format !== EContentPaneFormat['my-classes-students']);
      }
    }

    if (this._isGuest) {
      this.panes = this.panes.filter((el) => el.format !== EContentPaneFormat['my-classes-students']);
    }
    this.detectChanges();
  }

  public get isNewsHeader(): boolean {
    return ['news', 'my-classes', 'my-library', 'favorites'].includes(this.contentFilter?.baseRoute.split('/')[0]);
  }

  public isMeVersion() {
    return this.features.tenant === TenantEnum.onclass_me;
  }

  public trackByFn(index: any, pane: IContentPane) {
    return pane.order;
  }

  public ngOnDestroy(): void {
    this.loadContentBlocksSubject.complete();
    super.ngOnDestroy();
  }
}
